import type { Price } from '@maestro/data-layer'

import { theme } from '@maestro/local/theme'

export function buildPrice(
  currency: string,
  centAmount: number,
  discountedCentAmount?: number,
  discountedEndDate?: string,
  memberCentAmount?: number
): Price {
  const isDiscounted = discountedCentAmount !== undefined
  return {
    currency,
    amount: centAmount / 100,
    centAmount,
    ...(isDiscounted && { discountedAmount: discountedCentAmount / 100 }),
    ...(discountedEndDate && { discountedEndDate }),
    ...(memberCentAmount !== undefined && {
      memberAmount: memberCentAmount / 100,
    }),
  }
}

/**
 * Utility function to divide a number into integer and decimal parts.
 */
export function divideAmount(amount: number): {
  integerPrice: number
  decimalPrice: string
} {
  const parsedPrice = amount.toFixed(2)
  const [integer, decimal] = parsedPrice.split('.')
  const parsedInteger = parseInt(integer, 10)

  return {
    integerPrice: parsedInteger,
    decimalPrice: decimal,
  }
}

/**
 * Utility function to format numbers with thousand separators.
 */
export function formatWithThousandSeparator(
  value: number,
  separator: string
): string {
  // Use 'en-US' locale to get consistent comma separators (e.g., 1,234)
  // then replace with custom separator (e.g., space for NOK: 1 234)
  return new Intl.NumberFormat('en-US').format(value).replace(/,/g, separator)
}

// display price as string, keeping the same logic
// of converting price as Price component does
export function formatPriceAsText(price: Price): string {
  const {
    priceConfig: {
      showThousandSeparator,
      thousandSeparator,
      showDecimals,
      separator,
      prefix,
      suffix,
    },
  } = theme()

  const { integerPrice: integerPriceProps, decimalPrice: decimalPriceProps } =
    divideAmount(price?.amount || 0)

  let integerPrice = integerPriceProps
  if (!decimalPriceProps) {
    const parts = `${integerPriceProps}`.split(/[.,]/)
    integerPrice = parseInt(parts[0], 10)
  }

  // Apply thousand separator formatting if enabled
  const formattedIntegerPrice =
    showThousandSeparator && integerPrice >= 1000
      ? formatWithThousandSeparator(integerPrice, thousandSeparator)
      : integerPrice

  const decimalPrice = showDecimals ? `${separator}${decimalPriceProps}` : ''

  const isDecimalZero = decimalPrice === `${separator}00`

  return (
    (prefix || '') +
    formattedIntegerPrice +
    ((!isDecimalZero && decimalPrice) || '') +
    ((isDecimalZero && suffix) || '')
  )
}
